import { useEffect, useRef } from 'react'

import type { LicenseType } from '@ebalance/proto/bfftypes/v1/license_type_pb'

import {
  useIssuerOptions,
  useIssuerOptionsPending,
} from '~/components/bidding/common/IssuerSelector'
import type { IssuerOption } from '~/components/bidding/common/bidIssuer'
import { findIssuerOption } from '~/components/bidding/common/bidIssuer'

type Params = {
  deliveryDate: Date
  /** 新規作成で開いている間だけ true (編集・再入札・複製は元の札のエリアを保つ)。 */
  isActive: boolean
  /** form に今入っている区分。 render 時の watch 値は届いた直後に古いことがあるので都度読む。 */
  readIssuerType: () => LicenseType
  clearAreasUnselectableForIssuer: (option: IssuerOption | undefined) => void
}

/**
 * 区分の選択肢が取得中のまま開いた札作成モーダルで、 選択肢が届いた時に、 その区分で
 * 選べない初期エリアを外す (AC-BSP-123)。
 *
 * ツールバーの区分を初期値で渡すと、 届いた選択肢にその区分があれば既定選択の onChange が
 * 走らず、 区分の切替で外す経路 (AC-BSP-005b) を通らない。 開いた時に取得済みなら初期値の
 * 判定で足りるので、 取得中 → 取得済みに変わった 1 回だけ働く (後からの再取得では外さない。
 * 開いた後に未登録になったエリアは選択不可の表示で残す既存の扱いを変えないため)。
 */
export const useClearAreasWhenIssuerOptionsArrive = ({
  deliveryDate,
  isActive,
  readIssuerType,
  clearAreasUnselectableForIssuer,
}: Params): void => {
  const options = useIssuerOptions(deliveryDate)
  const isPending = useIssuerOptionsPending(deliveryDate)
  const wasPendingRef = useRef(isPending)
  const readIssuerTypeRef = useRef(readIssuerType)
  readIssuerTypeRef.current = readIssuerType
  useEffect(() => {
    const arrived = wasPendingRef.current && !isPending
    wasPendingRef.current = isPending
    if (!arrived || !isActive) return
    clearAreasUnselectableForIssuer(
      findIssuerOption(options, readIssuerTypeRef.current()),
    )
  }, [isPending, isActive, options, clearAreasUnselectableForIssuer])
}
